import React, { useEffect, useCallback } from "react";
import { API_URL as SERVER } from "../config";

interface LightboxProps {
  images: { id: number; url: string }[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}

export default function Lightbox({ images, index, onClose, onIndexChange }: LightboxProps) {
  const prev = useCallback(
    () => onIndexChange((index - 1 + images.length) % images.length),
    [index, images.length, onIndexChange],
  );
  const next = useCallback(
    () => onIndexChange((index + 1) % images.length),
    [index, images.length, onIndexChange],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={lightboxCloseBtn}
      >
        ×
      </button>
      <div style={{ position: "absolute", top: 20, left: 24, color: "#fff", fontSize: 14 }}>
        {index + 1} / {images.length}
      </div>

      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous"
          style={{ ...lightboxNavBtn, left: 20 }}
        >
          ‹
        </button>
      )}

      <img
        src={`${SERVER}${images[index].url}`}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw", maxHeight: "88vh",
          objectFit: "contain", boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
          borderRadius: 4,
        }}
      />

      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next"
          style={{ ...lightboxNavBtn, right: 20 }}
        >
          ›
        </button>
      )}
    </div>
  );
}

const lightboxNavBtn: React.CSSProperties = {
  position: "absolute", top: "50%", transform: "translateY(-50%)",
  width: 48, height: 48, borderRadius: "50%",
  background: "rgba(255,255,255,0.1)", color: "#fff", border: "none",
  fontSize: 32, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
};

const lightboxCloseBtn: React.CSSProperties = {
  position: "absolute", top: 20, right: 24,
  width: 40, height: 40, borderRadius: "50%",
  background: "rgba(255,255,255,0.1)", color: "#fff", border: "none",
  fontSize: 28, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
};
